import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Bell, Mail, Smartphone, Volume2, VolumeX } from 'lucide-react';
import { useSimpleProfile } from '@/hooks/useSimpleProfile';
import { useToast } from '@/hooks/use-toast';

const NotificationPreferences = () => {
  const { profile, updateProfile } = useSimpleProfile();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // Get current notification preferences with defaults
  const preferences = profile?.notification_preferences || {
    order_updates: true,
    email_marketing: true,
    product_recommendations: true,
    security_alerts: true,
    price_alerts: false,
    newsletter: true,
    app_notifications: true,
    email_notifications: true,
    sms_notifications: false,
    sound_enabled: true,
  };

  const handlePreferenceChange = async (key: string, value: boolean) => {
    setIsLoading(true);
    try {
      await updateProfile({
        notification_preferences: {
          ...preferences,
          [key]: value
        }
      });
      
      toast({
        title: "Preferences Updated",
        description: "Your notification preferences have been saved.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const notificationCategories = [
    {
      title: "Order & Account Updates",
      description: "Notifications about your orders and account activity",
      icon: Bell,
      preferences: [
        { key: 'order_updates', label: 'Order Status Updates', description: 'Get notified when your order status changes' },
        { key: 'security_alerts', label: 'Security Alerts', description: 'Important security notifications about your account' },
        { key: 'price_alerts', label: 'Price Drop Alerts', description: 'Get notified when items in your wishlist go on sale' },
      ]
    },
    {
      title: "Marketing & Promotions",
      description: "Promotional content and product recommendations",
      icon: Mail,
      preferences: [
        { key: 'email_marketing', label: 'Marketing Emails', description: 'Promotional offers and sales notifications' },
        { key: 'product_recommendations', label: 'Product Recommendations', description: 'Personalized product suggestions' },
        { key: 'newsletter', label: 'Newsletter', description: 'Weekly newsletter with latest products and news' },
      ]
    }
  ];

  const deliveryMethods = [
    { key: 'app_notifications', label: 'Push Notifications', description: 'Receive notifications in the app', icon: Smartphone },
    { key: 'email_notifications', label: 'Email Notifications', description: 'Receive notifications via email', icon: Mail },
    { key: 'sms_notifications', label: 'SMS Notifications', description: 'Receive notifications via text message', icon: Smartphone },
  ];

  return (
    <div className="space-y-6">
      {/* Delivery Methods */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Delivery Methods
          </CardTitle>
          <CardDescription>
            Choose how you want to receive notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {deliveryMethods.map((method) => (
            <div key={method.key} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <method.icon className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label className="text-sm font-medium">{method.label}</Label>
                  <p className="text-xs text-muted-foreground">{method.description}</p>
                </div>
              </div>
              <Switch
                checked={preferences[method.key] || false}
                onCheckedChange={(checked) => handlePreferenceChange(method.key, checked)}
                disabled={isLoading}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Sound Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {preferences.sound_enabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            Sound Settings
          </CardTitle>
          <CardDescription>
            Control notification sounds
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Notification Sounds</Label>
              <p className="text-xs text-muted-foreground">Play sound when notifications arrive</p>
            </div>
            <Switch
              checked={preferences.sound_enabled || false}
              onCheckedChange={(checked) => handlePreferenceChange('sound_enabled', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Categories */}
      {notificationCategories.map((category, index) => (
        <Card key={category.title}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <category.icon className="h-5 w-5" />
              {category.title}
            </CardTitle>
            <CardDescription>{category.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {category.preferences.map((pref, prefIndex) => (
              <div key={pref.key}>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">{pref.label}</Label>
                    <p className="text-xs text-muted-foreground">{pref.description}</p>
                  </div>
                  <Switch
                    checked={preferences[pref.key] || false}
                    onCheckedChange={(checked) => handlePreferenceChange(pref.key, checked)}
                    disabled={isLoading}
                  />
                </div>
                {prefIndex < category.preferences.length - 1 && <Separator className="mt-4" />}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Manage all your notification preferences at once</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              const allKeys = ['order_updates', 'email_marketing', 'product_recommendations', 'security_alerts', 'price_alerts', 'newsletter'];
              allKeys.forEach(key => handlePreferenceChange(key, true));
            }}
            disabled={isLoading}
          >
            Enable All
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const nonEssentialKeys = ['email_marketing', 'product_recommendations', 'price_alerts', 'newsletter'];
              nonEssentialKeys.forEach(key => handlePreferenceChange(key, false));
            }}
            disabled={isLoading}
          >
            Essential Only
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPreferences;